/**
 * The subset of Markdown the assistant is told to write (ADR 379): paragraphs,
 * bullet lists, pipe tables, `**bold**` and `` `code` ``. No HTML, no links —
 * the reply is text, the provenance footer carries the link. Parsing is pure;
 * MiniMarkdown.tsx renders the blocks.
 */
export type Inline = { kind: "text" | "bold" | "code"; text: string };
export type Block =
  | { kind: "p"; inlines: Inline[] }
  | { kind: "ul"; items: Inline[][] }
  | { kind: "table"; header: Inline[][]; rows: Inline[][][] };

export function parseInlines(s: string): Inline[] {
  const out: Inline[] = [];
  const re = /(\*\*([^*]+)\*\*)|(`([^`]+)`)/g;
  let last = 0;
  for (let m = re.exec(s); m; m = re.exec(s)) {
    if (m.index > last) out.push({ kind: "text", text: s.slice(last, m.index) });
    if (m[2] !== undefined) out.push({ kind: "bold", text: m[2] });
    else out.push({ kind: "code", text: m[4] });
    last = m.index + m[0].length;
  }
  if (last < s.length) out.push({ kind: "text", text: s.slice(last) });
  return out;
}

const isTableRow = (l: string) => /^\s*\|.*\|\s*$/.test(l);
const isSeparator = (l: string) => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(l);
const cells = (l: string) => l.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => parseInlines(c.trim()));

export function parseBlocks(md: string): Block[] {
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }
    if (isTableRow(line) && i + 1 < lines.length && isSeparator(lines[i + 1])) {
      const header = cells(line);
      const rows: Inline[][][] = [];
      i += 2;
      while (i < lines.length && isTableRow(lines[i])) { rows.push(cells(lines[i])); i++; }
      blocks.push({ kind: "table", header, rows });
      continue;
    }
    if (/^\s*[-*•]\s+/.test(line)) {
      const items: Inline[][] = [];
      while (i < lines.length && /^\s*[-*•]\s+/.test(lines[i])) {
        items.push(parseInlines(lines[i].replace(/^\s*[-*•]\s+/, "")));
        i++;
      }
      blocks.push({ kind: "ul", items });
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && lines[i].trim() && !/^\s*[-*•]\s+/.test(lines[i]) && !(isTableRow(lines[i]) && isSeparator(lines[i + 1] ?? ""))) {
      para.push(lines[i].replace(/^#+\s*/, "").trim());
      i++;
    }
    blocks.push({ kind: "p", inlines: parseInlines(para.join(" ")) });
  }
  return blocks;
}
